import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getCachedPortalBootstrap, getPortalBootstrapCacheKey, setCachedPortalBootstrap } from "../portalRuntimeCache";
import { getCachedShipmentDetails, getCachedShipmentList, setCachedShipmentDetails, setCachedShipmentList } from "../portalPageDataCache";
import ShipmentFilterBar, { type ShipmentFilterBarFilter } from "../components/ShipmentFilterBar";
import {
  Anchor,
  AlertTriangle,
  Boxes,
  CalendarDays,
  Info,
  MapPinned,
  PackageCheck,
  RefreshCw,
  Search,
  ShipWheel,
  Truck,
} from "lucide-react";

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

type RuntimeCustomer = {
  customerId: string;
  customerKey?: string;
  customerName?: string;
};

type RuntimeBootstrap = {
  portalKey: string;
  portalName?: string;
  role?: string;
  canSelectCustomer?: boolean;
  selectedCustomer?: RuntimeCustomer | null;
  customers?: RuntimeCustomer[];
  enabledPages?: { shipments?: boolean; analytics?: boolean; jpc?: boolean; admin?: boolean };
};

type Shipment = {
  id?: string;
  reference?: string;
  customerReference?: string;
  jordexReference?: string;
  status?: string;
  pol?: string;
  pod?: string;
  carrier?: string;
  vessel?: string;
  eta?: string;
  originalEta?: string;
  updatedEta?: string;
  etd?: string;
  originalEtd?: string;
  updatedEtd?: string;
  createdAt?: string;
  movementType?: string;
  containers?: unknown[];
  masters?: unknown[];
  documents?: unknown[];
  customerName?: string;
  consigneeName?: string;
  consigneeAddress?: { name?: string | null } | null;
  modality?: string;
  loadType?: string;
  containerCount?: number;
  blNumber?: string;
  transitDays?: number | null;
  originPort?: ShipmentPort | null;
  destinationPort?: ShipmentPort | null;
};

type ShipmentPort = {
  location?: string | null;
  country?: string | null;
  unLoCode?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
};

type ContainerRoutingEvent = { id?: string | null; eventType?: string | null; section?: string | null; location?: string | null; timestamp?: string | null; actualDate?: string | null; plannedDate?: string | null };
type ShipmentContainer = { containerId?: string | null; number?: string | null; containerNumber?: string | null; routing?: ContainerRoutingEvent[] };

type ShipmentSortField = "reference" | "origin" | "destination" | "consignee" | "carrier" | "etd" | "originalEta" | "updatedEta" | "mode" | "status";
type SortDirection = "asc" | "desc";

function MetricInfo({ children }: { children: React.ReactNode }) {
  return <span className="group relative inline-flex"><button type="button" aria-label="Show calculation details" className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 outline-none transition hover:text-orange-500 focus-visible:ring-2 focus-visible:ring-orange-400"><Info className="h-3.5 w-3.5" aria-hidden="true" /></button><span role="tooltip" className="pointer-events-none absolute left-0 top-full z-30 mt-2 hidden w-64 rounded-md bg-slate-950 px-3 py-2 text-left text-[11px] font-medium normal-case leading-relaxed tracking-normal text-white shadow-xl group-hover:block group-focus-within:block dark:bg-slate-700">{children}</span></span>;
}

type RuntimePortalPageProps = {
  apiFetch: ApiFetch;
  portalKeyOverride?: string;
  initialCustomerId?: string;
  onSelectedCustomerChange?: (customerId: string) => void;
  onPortalContext?: (context: { role?: string; customerId?: string; enabledPages?: { shipments?: boolean; analytics?: boolean; jpc?: boolean; bookings?: boolean; quotes?: boolean; admin?: boolean } }) => void;
  onAdminToolbarChange?: (toolbar: AdminToolbar) => void;
  onAccessDenied?: () => void;
};

export type AdminToolbar = {
  visible: boolean;
  customerId: string;
  customers: RuntimeCustomer[];
  loading: boolean;
  refreshing: boolean;
  onCustomerChange: (customerId: string) => void;
  onReload: () => void;
  onRefresh: () => void;
};

// Deliberately in memory only: navigating between portal pages is instant,
// but customer data is never persisted to local or session storage.
function statusTone(status: string | undefined): string {
  const value = String(status || "").toLowerCase();

  if (value === "attention") {
    return "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300";
  }

  if (value === "planned") {
    return "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-500/30 dark:bg-fuchsia-500/10 dark:text-fuchsia-300";
  }

  if (value === "in transit") {
    return "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300";
  }

  if (value === "canceled" || value === "unknown") {
    return "border-slate-200 bg-slate-50 text-slate-600 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400";
  }

  return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300";
}

function formatDate(value: string | undefined): string {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).replace(/ /g, "\u00a0");
}

function latestEta(shipment: Shipment): string | undefined {
  return shipment.updatedEta || shipment.eta;
}

function latestEtd(shipment: Shipment): string | undefined {
  return shipment.updatedEtd || shipment.etd;
}

// The operational date depends on the direction of the shipment. This is
// intentionally kept separate from the dates displayed in the table so that
// each filter option always represents the same business rule.
function shipmentFilterDate(shipment: Shipment): string | undefined {
  const movementType = String(shipment.movementType || "").trim().toUpperCase();

  if (movementType === "IMPORT" || movementType === "CROSSTRADE") {
    return latestEta(shipment) || shipment.createdAt;
  }

  if (movementType === "EXPORT") {
    return latestEtd(shipment) || shipment.createdAt;
  }

  return shipment.createdAt;
}

function shipmentFilterYear(shipment: Shipment): string {
  const date = shipmentFilterDate(shipment) ? new Date(shipmentFilterDate(shipment)!) : null;
  return date && !Number.isNaN(date.getTime()) ? String(date.getFullYear()) : "";
}

function shipmentFilterMonth(shipment: Shipment): string {
  const date = shipmentFilterDate(shipment) ? new Date(shipmentFilterDate(shipment)!) : null;
  return date && !Number.isNaN(date.getTime())
    ? String(date.getMonth() + 1).padStart(2, "0")
    : "";
}

function parseFilterValues(value: string | null): string[] {
  return String(value || "").split("|").map((item) => item.trim()).filter(Boolean);
}

function matchesFilter(values: string[], value: string | undefined): boolean {
  return !values.length || values.includes(String(value || ""));
}

function dateDeltaDays(original?: string, updated?: string): number | null {
  if (!original || !updated) return null;

  const toUtcDay = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return Number.NaN;
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  };
  const difference = (toUtcDay(updated) - toUtcDay(original)) / 86_400_000;
  return Number.isFinite(difference) ? difference : null;
}

function hasChangedEta(shipment: Shipment): boolean {
  const difference = dateDeltaDays(shipment.originalEta, latestEta(shipment));
  return difference !== null && difference !== 0;
}

function dateDeltaLabel(original?: string, updated?: string): string | null {
  const difference = dateDeltaDays(original, updated);
  if (difference === null || difference === 0) return null;
  return `${difference > 0 ? "+" : ""}${difference}d`;
}

function dateDeltaTone(original?: string, updated?: string): string {
  const difference = dateDeltaDays(original, updated);
  if (difference === null || difference === 0) return "text-slate-500 dark:text-slate-400";
  return difference > 0 ? "text-rose-500" : "text-emerald-500";
}

function UpdatedDateWithDelta({ original, updated }: { original?: string; updated?: string }) {
  const delta = dateDeltaLabel(original, updated);
  return <span className="inline-flex whitespace-nowrap flex-col"><span>{formatDate(updated)}</span>{delta && <span className={`mt-0.5 text-[10px] font-black ${dateDeltaTone(original, updated)}`}>{delta}</span>}</span>;
}

function displayStatus(shipment: Shipment): string {
  const sourceStatus = String(shipment.status || "").trim().toLowerCase();
  if (sourceStatus.includes("cancel")) return "Canceled";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const etd = shipment.etd ? new Date(shipment.etd).getTime() : Number.NaN;
  const eta = latestEta(shipment) ? new Date(latestEta(shipment)!).getTime() : Number.NaN;
  if (Number.isFinite(eta) && eta < today.getTime()) return "Arrived";
  const etdDifference = dateDeltaDays(shipment.originalEtd, latestEtd(shipment));
  if (Number.isFinite(eta) && eta > today.getTime() && (hasChangedEta(shipment) || (etdDifference !== null && etdDifference !== 0))) return "Attention";
  if (Number.isFinite(etd) && etd > today.getTime()) return "Planned";
  if (Number.isFinite(etd) && Number.isFinite(eta) && etd <= today.getTime() && today.getTime() <= eta) return "In Transit";
  return "Unknown";
}

function consigneeName(shipment: Shipment): string {
  return shipment.consigneeName || shipment.consigneeAddress?.name || "";
}

function matchesSearch(shipment: Shipment, search: string): boolean {
  if (!search.trim()) return true;

  const haystack = [
    shipment.reference,
    shipment.customerReference,
    shipment.jordexReference,
    shipment.status,
    shipment.pol,
    shipment.pod,
    shipment.carrier,
    shipment.vessel,
    shipment.customerName,
    consigneeName(shipment),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(search.trim().toLowerCase());
}

function shipmentKey(shipment: Shipment, index = 0): string {
  return String(shipment.id || shipment.reference || shipment.jordexReference || index);
}

function shipmentSortValue(shipment: Shipment, field: ShipmentSortField): string | number {
  switch (field) {
    case "reference": return shipment.reference || shipment.customerReference || shipment.jordexReference || "";
    case "origin": return routeLocation(shipment.originPort, shipment.pol);
    case "destination": return routeLocation(shipment.destinationPort, shipment.pod);
    case "consignee": return consigneeName(shipment);
    case "carrier": return `${shipment.carrier || ""} ${shipment.vessel || ""}`;
    case "etd": return new Date(latestEtd(shipment) || 0).getTime() || 0;
    case "originalEta": return new Date(shipment.originalEta || 0).getTime() || 0;
    case "updatedEta": return new Date(latestEta(shipment) || 0).getTime() || 0;
    case "mode": return shipment.loadType || shipment.modality || "";
    case "status": return displayStatus(shipment);
  }
}

function portLabel(port: ShipmentPort | null | undefined, fallback?: string): string {
  if (!port) return fallback || "Not available";

  return [port.location || port.unLoCode, port.country]
    .filter(Boolean)
    .join(", ") || fallback || "Not available";
}

const countryCodes: Record<string, string> = {
  australia: "AU", belgium: "BE", brazil: "BR", canada: "CA", china: "CN",
  denmark: "DK", france: "FR", germany: "DE", india: "IN", indonesia: "ID",
  italy: "IT", japan: "JP", malaysia: "MY", mexico: "MX", netherlands: "NL",
  norway: "NO", poland: "PL", portugal: "PT", singapore: "SG", southafrica: "ZA",
  southkorea: "KR", spain: "ES", sweden: "SE", switzerland: "CH", thailand: "TH",
  turkey: "TR", uk: "GB", unitedarabemirates: "AE", unitedkingdom: "GB",
  unitedstates: "US", usa: "US", vietnam: "VN",
};

function countryCode(port: ShipmentPort | null | undefined): string {
  const country = String(port?.country || "").trim();
  if (/^[a-z]{2}$/i.test(country)) return country.toUpperCase();

  const unLoCode = String(port?.unLoCode || "").trim();
  if (/^[a-z]{2}/i.test(unLoCode)) return unLoCode.slice(0, 2).toUpperCase();

  return countryCodes[country.toLowerCase().replace(/[^a-z]/g, "")] || "";
}

function countryFlag(port: ShipmentPort | null | undefined): React.ReactNode {
  const code = countryCode(port);
  if (!code) return null;

  return <img
    className="h-3 w-4 shrink-0 rounded-[1px] object-cover"
    src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`}
    alt={`${port?.country || code} flag`}
  />;
}

function routeLocation(port: ShipmentPort | null | undefined, fallback?: string): string {
  return String(port?.location || fallback || port?.unLoCode || "-").replace(/\s+/g, "\u00a0");
}

function coordinate(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

type MapPoint = { latitude: number; longitude: number };

const geocodeCache = new Map<string, Promise<MapPoint | null>>();
const MAX_GEOCODE_CACHE_ENTRIES = 200;

function lookupCity(place: string): Promise<MapPoint | null> {
  const cached = geocodeCache.get(place);
  if (cached) return cached;

  const request = fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(place)}`)
    .then((response) => response.ok ? response.json() : [])
    .then((rows: Array<{ lat?: string; lon?: string }>) => {
      const latitude = coordinate(rows[0]?.lat);
      const longitude = coordinate(rows[0]?.lon);
      return latitude === null || longitude === null ? null : { latitude, longitude };
    })
    .catch(() => null);
  geocodeCache.set(place, request);
  while (geocodeCache.size > MAX_GEOCODE_CACHE_ENTRIES) {
    const oldest = geocodeCache.keys().next().value;
    if (!oldest) break;
    geocodeCache.delete(oldest);
  }
  return request;
}

function useDarkTheme(): boolean {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setDark(root.classList.contains("dark")));
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return dark;
}

function LiveShipmentMap({ origin, destination, originName, destinationName }: { origin: MapPoint; destination: MapPoint; originName: string; destinationName: string }) {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const routeLayerRef = useRef<L.LayerGroup | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const darkTheme = useDarkTheme();
  const cartoBasemapKey = process.env.REACT_APP_CARTO_BASEMAP_KEY?.trim();

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) return;
    const map = L.map(mapElementRef.current, { zoomControl: false, attributionControl: true, scrollWheelZoom: false });
    map.attributionControl.setPrefix(false);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    tileLayerRef.current?.remove();
    const useCartoBasemap = Boolean(darkTheme && cartoBasemapKey);
    tileLayerRef.current = L.tileLayer(
      useCartoBasemap
        ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(cartoBasemapKey!)}`
        : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution: useCartoBasemap
          ? "© OpenStreetMap contributors © CARTO"
          : "© OpenStreetMap contributors",
        maxZoom: 18,
      }
    ).addTo(map);
  }, [cartoBasemapKey, darkTheme]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    routeLayerRef.current?.remove();
    const layer = L.layerGroup().addTo(map);
    routeLayerRef.current = layer;
    const from: L.LatLngExpression = [origin.latitude, origin.longitude];
    const to: L.LatLngExpression = [destination.latitude, destination.longitude];
    const endpointTooltip = (label: string, location: string) => {
      const content = document.createElement("div");
      const endpointLabel = document.createElement("strong");
      const endpointLocation = document.createElement("span");
      endpointLabel.className = "shipment-map-endpoint__label";
      endpointLocation.className = "shipment-map-endpoint__location";
      endpointLabel.textContent = label;
      endpointLocation.textContent = location;
      content.append(endpointLabel, endpointLocation);
      return content;
    };
    L.circleMarker(from, { radius: 7, color: "#fff", weight: 2, fillColor: "#f97316", fillOpacity: 1 })
      .bindTooltip(endpointTooltip("Origin", originName), { permanent: true, direction: "top", offset: [0, -9], className: "shipment-map-endpoint shipment-map-endpoint--origin" })
      .addTo(layer);
    L.circleMarker(to, { radius: 7, color: "#fff", weight: 2, fillColor: "#38bdf8", fillOpacity: 1 })
      .bindTooltip(endpointTooltip("Destination", destinationName), { permanent: true, direction: "bottom", offset: [0, 9], className: "shipment-map-endpoint shipment-map-endpoint--destination" })
      .addTo(layer);
    map.fitBounds(L.latLngBounds([from, to]), { padding: [44, 44], maxZoom: 5 });
    const resizeTimer = window.setTimeout(() => {
      if (mapRef.current === map && mapElementRef.current) map.invalidateSize();
    }, 0);
    return () => window.clearTimeout(resizeTimer);
  }, [destination, destinationName, origin, originName]);

  return <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-950 text-white dark:border-white/10"><div ref={mapElementRef} className="shipment-map h-56 w-full bg-slate-900" /></div>;
}

function ContainerRoutingTimeline({ containers, loading = false }: { containers?: unknown[]; loading?: boolean }) {
  const shipmentContainers = Array.isArray(containers) ? containers as ShipmentContainer[] : [];
  const routedContainers = Array.from(shipmentContainers.reduce<Map<string, ShipmentContainer>>((uniqueContainers, container) => {
    if (!Array.isArray(container.routing) || !container.routing.length) return uniqueContainers;
    const containerKey = String(container.number || container.containerNumber || container.containerId || "").trim().toLowerCase();
    if (!containerKey) return uniqueContainers;
    const existing = uniqueContainers.get(containerKey);
    if (!existing) {
      uniqueContainers.set(containerKey, { ...container, routing: [...container.routing] });
      return uniqueContainers;
    }
    existing.routing = Array.from(new Map(
      [...(existing.routing || []), ...container.routing]
        .map((event) => [String(event.id || `${event.eventType}:${event.timestamp}:${event.location || ""}`), event])
    ).values());
    return uniqueContainers;
  }, new Map()).values());
  const [activeContainerIndex, setActiveContainerIndex] = useState(0);

  useEffect(() => {
    if (activeContainerIndex >= routedContainers.length) setActiveContainerIndex(0);
  }, [activeContainerIndex, routedContainers.length]);

  const eventLabel = (event: ContainerRoutingEvent) => `${event.section ? `${event.section.charAt(0)}${event.section.slice(1).toLowerCase()} · ` : ""}${(event.eventType || "Routing update").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase())}`;

  return <section className="mt-2">
    <p className="text-[10px] font-black uppercase tracking-[.16em] text-orange-500">Container routing</p>
    {!routedContainers.length ? <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{loading ? "Loading routing milestones…" : "No routing milestones are available for this shipment yet."}</p> : null}
    {routedContainers.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1">{routedContainers.map((container, containerIndex) => { const label = container.number || container.containerNumber || `Container ${containerIndex + 1}`; return <button key={`${label}-${containerIndex}`} type="button" onClick={() => setActiveContainerIndex(containerIndex)} className={`border px-2 py-1 text-[10px] font-bold leading-none transition ${containerIndex === activeContainerIndex ? "border-orange-400 bg-orange-500/10 text-orange-500" : "border-slate-200 text-slate-500 hover:border-orange-300 dark:border-white/10"}`}>{label}</button>; })}</div>}
    <div className="mt-1.5">{routedContainers.filter((_, containerIndex) => containerIndex === activeContainerIndex).map((container, containerIndex) => {
      const routingDate = (event: ContainerRoutingEvent) => event.actualDate || event.plannedDate || null;
      const milestoneOrder: Record<string, number> = {
        PICKUP_CUSTOMER: 0,
        PICKUP_TERMINAL_ORIGIN: 1,
        DROP_OFF_TERMINAL_ORIGIN: 2,
        PICKUP_TERMINAL_DESTINATION: 3,
        DELIVERY_CUSTOMER: 4,
        RETURN_TERMINAL: 5,
      };
      const milestoneIndex = (event: ContainerRoutingEvent) => {
        const eventType = String(event.eventType || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
        const section = String(event.section || "").trim().toUpperCase();
        const normalizedEventType = eventType === "PICKUP_TERMINAL"
          ? `PICKUP_TERMINAL_${section}`
          : eventType === "DROP_OFF_TERMINAL"
            ? `DROP_OFF_TERMINAL_${section || "ORIGIN"}`
            : eventType;
        return milestoneOrder[normalizedEventType] ?? Number.MAX_SAFE_INTEGER;
      };
      const events = [...(container.routing || [])]
        .map((event) => ({ ...event, timestamp: routingDate(event) }))
        .sort((a, b) => milestoneIndex(a) - milestoneIndex(b) || String(a.timestamp || "").localeCompare(String(b.timestamp || "")));
      return <ol key={`${container.number || container.containerNumber || "container"}-${containerIndex}`} className="space-y-1 border-l border-slate-300 pl-2.5 dark:border-white/20">{events.map((event, eventIndex) => { const isActual = Boolean(event.actualDate); const hasDate = Boolean(event.timestamp); return <li key={`${event.id || event.timestamp || eventIndex}`} className="relative grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 text-[11px] leading-4"><span className={`absolute -left-[14px] top-1.5 h-1.5 w-1.5 rounded-full ${isActual ? "bg-orange-500" : "bg-slate-400"}`} /><p className="min-w-0 font-semibold text-slate-700 dark:text-slate-200">{eventLabel(event)}{event.location ? <span className="font-normal text-slate-500 dark:text-slate-400"> · {event.location}</span> : null}</p><time className={`whitespace-nowrap text-[10px] leading-4 ${hasDate ? (isActual ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400") : "text-slate-400 dark:text-slate-500"}`}>{hasDate ? <><span className="mr-1 font-bold">{isActual ? "Actual" : "Planned"}</span>{formatDate(event.timestamp || undefined)}</> : "-"}</time></li>; })}</ol>;
    })}</div>
  </section>;
}

function RouteMap({ shipment }: { shipment: Shipment }) {
  const originName = portLabel(shipment.originPort, shipment.pol);
  const destinationName = portLabel(shipment.destinationPort, shipment.pod);
  const [points, setPoints] = useState<{ origin: MapPoint | null; destination: MapPoint | null } | null>(null);

  useEffect(() => {
    let active = true;
    const storedOrigin = { latitude: coordinate(shipment.originPort?.latitude), longitude: coordinate(shipment.originPort?.longitude) };
    const storedDestination = { latitude: coordinate(shipment.destinationPort?.latitude), longitude: coordinate(shipment.destinationPort?.longitude) };
    const resolve = async () => {
      const origin = storedOrigin.latitude !== null && storedOrigin.longitude !== null
        ? { latitude: storedOrigin.latitude, longitude: storedOrigin.longitude }
        : await lookupCity(originName);
      const destination = storedDestination.latitude !== null && storedDestination.longitude !== null
        ? { latitude: storedDestination.latitude, longitude: storedDestination.longitude }
        : await lookupCity(destinationName);
      if (active) setPoints({ origin, destination });
    };
    setPoints(null);
    void resolve();
    return () => { active = false; };
  }, [shipment.id, shipment.originPort?.latitude, shipment.originPort?.longitude, shipment.destinationPort?.latitude, shipment.destinationPort?.longitude, originName, destinationName]);

  const origin = points?.origin;
  const destination = points?.destination;
  if (origin && destination) return <LiveShipmentMap origin={origin} destination={destination} originName={originName} destinationName={destinationName} />;
  const hasRoute = Boolean(origin && destination);
  const latitudePadding = hasRoute ? Math.max(1, Math.abs(origin!.latitude - destination!.latitude) * 0.22) : 1;
  const longitudePadding = hasRoute ? Math.max(1.5, Math.abs(origin!.longitude - destination!.longitude) * 0.16) : 1.5;
  const bounds = hasRoute ? [
    Math.max(-180, Math.min(origin!.longitude, destination!.longitude) - longitudePadding),
    Math.max(-85, Math.min(origin!.latitude, destination!.latitude) - latitudePadding),
    Math.min(180, Math.max(origin!.longitude, destination!.longitude) + longitudePadding),
    Math.min(85, Math.max(origin!.latitude, destination!.latitude) + latitudePadding),
  ] : null;
  const mapUrl = bounds ? `https://www.openstreetmap.org/export/embed.html?bbox=${bounds.join("%2C")}&layer=mapnik` : null;
  const mercatorY = (latitude: number) => Math.log(Math.tan(Math.PI / 4 + (Math.max(-85, Math.min(85, latitude)) * Math.PI / 180) / 2));
  const pointFor = (point: MapPoint) => {
    if (!bounds) return null;
    const west = bounds[0];
    const south = bounds[1];
    const east = bounds[2];
    const north = bounds[3];
    const northY = mercatorY(north);
    const southY = mercatorY(south);
    return {
      x: ((point.longitude - west) / (east - west)) * 100,
      y: ((northY - mercatorY(point.latitude)) / (northY - southY)) * 100,
    };
  };
  const originPoint = origin ? pointFor(origin) : null;
  const destinationPoint = destination ? pointFor(destination) : null;

  return <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-950 text-white dark:border-white/10">
    <div className="relative h-40 overflow-hidden bg-slate-900">
      {mapUrl ? <><iframe title={`Map between ${originName} and ${destinationName}`} src={mapUrl} className="pointer-events-auto absolute inset-0 h-full w-full border-0" loading="lazy" />{originPoint && destinationPoint && <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" aria-label={`Origin ${originName}; destination ${destinationName}`}><circle cx={originPoint.x} cy={originPoint.y} r="3.5" fill="#fb923c" stroke="white" strokeWidth=".9" /><circle cx={destinationPoint.x} cy={destinationPoint.y} r="3.5" fill="#38bdf8" stroke="white" strokeWidth=".9" /></svg>}<div className="pointer-events-none absolute bottom-3 left-3 max-w-[45%] bg-slate-950/90 px-2 py-1.5 text-[10px] leading-tight text-white"><strong className="block uppercase tracking-[.12em] text-orange-300">Origin</strong><span className="block truncate font-bold">{originName}</span></div><div className="pointer-events-none absolute right-3 top-3 max-w-[45%] bg-slate-950/90 px-2 py-1.5 text-right text-[10px] leading-tight text-white"><strong className="block uppercase tracking-[.12em] text-sky-300">Destination</strong><span className="block truncate font-bold">{destinationName}</span></div></> : <div className="flex h-full flex-col items-center justify-center gap-2 p-5 text-center"><MapPinned className="h-6 w-6 text-orange-400" /><p className="text-sm font-bold">Locating {originName} and {destinationName}…</p><p className="text-xs text-slate-400">The map uses the port coordinates when available, then falls back to the city names.</p></div>}
    </div>
    <p className="border-t border-white/10 px-4 py-2 text-[10px] text-slate-400">© OpenStreetMap contributors · Origin and destination are shown independently.</p>
  </div>;
}

export default function RuntimePortalPage({
  apiFetch,
  portalKeyOverride,
  initialCustomerId,
  onSelectedCustomerChange,
  onPortalContext,
  onAdminToolbarChange,
  onAccessDenied,
}: RuntimePortalPageProps) {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const routePortalKey =
    portalKeyOverride ||
    params.portalKey ||
    searchParams.get("portalKey") ||
    "";

  const [bootstrap, setBootstrap] = useState<RuntimeBootstrap | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(initialCustomerId || "");
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(() => parseFilterValues(searchParams.get("status")));
  const [polFilter, setPolFilter] = useState(() => parseFilterValues(searchParams.get("pol")));
  const [podFilter, setPodFilter] = useState(() => parseFilterValues(searchParams.get("pod")));
  const [carrierFilter, setCarrierFilter] = useState(() => parseFilterValues(searchParams.get("carrier")));
  const [vesselFilter, setVesselFilter] = useState(() => parseFilterValues(searchParams.get("vessel")));
  const [consigneeFilter, setConsigneeFilter] = useState(() => parseFilterValues(searchParams.get("consignee")));
  const [modalityFilter, setModalityFilter] = useState(() => parseFilterValues(searchParams.get("modality") || "OCEAN"));
  const [yearFilter, setYearFilter] = useState(() => parseFilterValues(searchParams.get("year") || String(new Date().getFullYear())));
  const [monthFilter, setMonthFilter] = useState(() => parseFilterValues(searchParams.get("month")));
  const [sortField, setSortField] = useState<ShipmentSortField>("updatedEta");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(1);
  const [totalShipmentCount, setTotalShipmentCount] = useState(0);
  const [totalStatusCounts, setTotalStatusCounts] = useState<Record<string, number>>({});
  const [availableFilterOptions, setAvailableFilterOptions] = useState<Record<string, string[]>>({});
  const [selectedShipmentKey, setSelectedShipmentKey] = useState("");
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [shipmentDetails, setShipmentDetails] = useState<Record<string, Shipment>>({});
  const [containerCountsReady, setContainerCountsReady] = useState(false);
  const lastLoadedCustomerRef = useRef("");
  const containerCountsRequestRef = useRef(0);
  const shipmentListRequestRef = useRef(0);
  const shipmentListInFlightRef = useRef<{ key: string; promise: Promise<void> } | null>(null);
  const detailsPrefetchKeyRef = useRef("");
  const userSelectedShipmentRef = useRef(false);
  const selectedCustomerIdRef = useRef(selectedCustomerId);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const filters: Record<string, string | string[]> = { search, status: statusFilter, pol: polFilter, pod: podFilter, carrier: carrierFilter, vessel: vesselFilter, consignee: consigneeFilter, modality: modalityFilter, year: yearFilter, month: monthFilter };
    Object.entries(filters).forEach(([key, value]) => {
      const serialized = Array.isArray(value) ? value.join("|") : value;
      serialized ? next.set(key, serialized) : next.delete(key);
    });
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [search, statusFilter, polFilter, podFilter, carrierFilter, vesselFilter, consigneeFilter, modalityFilter, yearFilter, monthFilter, searchParams, setSearchParams]);

  useEffect(() => {
    selectedCustomerIdRef.current = selectedCustomerId;
  }, [selectedCustomerId]);

  useEffect(() => {
    if (selectedCustomerId) onSelectedCustomerChange?.(selectedCustomerId);
  }, [onSelectedCustomerChange, selectedCustomerId]);

  const customers = useMemo(
    () => bootstrap?.customers || [],
    [bootstrap?.customers]
  );
  const selectedCustomer = useMemo(() => {
    return (
      customers.find(
        (customer) => customer.customerId === selectedCustomerId
      ) ||
      bootstrap?.selectedCustomer ||
      null
    );
  }, [customers, selectedCustomerId, bootstrap]);

  const portalRequestHeaders = useMemo(() => {
    return routePortalKey ? { "X-Portal-Key": routePortalKey } : undefined;
  }, [routePortalKey]);

  const runtimeRequestHeaders = useCallback((customerId?: string) => {
    const headers = new Headers(portalRequestHeaders);
    // Bootstrap state is not committed yet during the first shipment request.
    // The server resolves and authorizes this selection on every request.
    if (customerId) {
      headers.set("X-Customer-Id", customerId);
    }
    return headers;
  }, [portalRequestHeaders]);

  const loadBootstrap = useCallback(async (force = false) => {
    const portalCacheKey = routePortalKey || window.location.hostname;
    const bootstrapCacheKey = getPortalBootstrapCacheKey(portalCacheKey, selectedCustomerIdRef.current);
    let nextBootstrap = force ? null : getCachedPortalBootstrap<RuntimeBootstrap>(bootstrapCacheKey);
    if (!nextBootstrap) {
      const response = await apiFetch("/api/foundry/runtime/bootstrap", {
        headers: runtimeRequestHeaders(selectedCustomerIdRef.current),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 403) {
          onAccessDenied?.();
        }
        throw new Error(data.error || "Portal bootstrap mislukt.");
      }
      nextBootstrap = data as RuntimeBootstrap;
      setCachedPortalBootstrap(bootstrapCacheKey, nextBootstrap);
    }
    const selectedCustomerIsAvailable = Boolean(nextBootstrap.customers?.some(
      (customer) => customer.customerId === selectedCustomerIdRef.current,
    ));
    const nextCustomerId =
      (selectedCustomerIsAvailable ? selectedCustomerIdRef.current : "") ||
      nextBootstrap.selectedCustomer?.customerId ||
      nextBootstrap.customers?.[0]?.customerId ||
      "";

    setBootstrap(nextBootstrap);
    onPortalContext?.({ role: nextBootstrap.role, customerId: nextCustomerId, enabledPages: nextBootstrap.enabledPages });
    setSelectedCustomerId(nextCustomerId);

    return {
      portalKey: nextBootstrap.portalKey,
      customerId: nextCustomerId,
    };
  }, [apiFetch, onAccessDenied, onPortalContext, runtimeRequestHeaders, routePortalKey]);

  // Retained for an explicit future count refresh; customer switching no
  // longer starts this all-shipment aggregation.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loadContainerCounts = useCallback(
    async (input: { customerId: string }) => {
      const requestId = ++containerCountsRequestRef.current;

      try {
        const response = await apiFetch(
          "/api/foundry/runtime/shipment-container-counts",
          { headers: runtimeRequestHeaders(input.customerId) }
        );
        const data = await response.json().catch(() => ({}));

        if (!response.ok || requestId !== containerCountsRequestRef.current) {
          return;
        }

        const counts = data.counts && typeof data.counts === "object"
          ? data.counts as Record<string, number>
          : {};

        setShipments((current) => {
          const shipments = current.map((shipment) => ({
            ...shipment,
            containerCount: Number(counts[shipmentKey(shipment)] || 0),
          }));
          setCachedShipmentList(`${routePortalKey || window.location.hostname}:${input.customerId}`, { shipments, containerCountsReady: true });
          return shipments;
        });
        setContainerCountsReady(true);
      } catch {
        // The shipment list remains usable even if count aggregation fails.
      }
    },
    [apiFetch, routePortalKey, runtimeRequestHeaders]
  );

  const loadShipments = useCallback(
    (input?: { customerId?: string; page?: number }, force = false, prefetch = false): Promise<void> => {
      const customerId = input?.customerId || "";
      const requestedPage = input?.page || page;
      const filterCacheKey = [search, statusFilter, polFilter, podFilter, carrierFilter, vesselFilter, consigneeFilter, modalityFilter, yearFilter, monthFilter].join("\u0000");
      const cacheKey = `${routePortalKey || window.location.hostname}:${customerId}:${filterCacheKey}:page:${requestedPage}`;
      if (!force && !prefetch && shipmentListInFlightRef.current?.key === cacheKey) {
        return shipmentListInFlightRef.current.promise;
      }

      const promise = (async () => {
        const requestId = prefetch ? 0 : ++shipmentListRequestRef.current;
        const cached = !force && customerId ? getCachedShipmentList<{ shipments: Shipment[]; containerCountsReady: boolean; totalShipmentCount: number; totalStatusCounts: Record<string, number>; filterOptions: Record<string, string[]> }>(cacheKey) : null;

        if (cached) {
          if (prefetch) return;
          setShipments(cached.shipments);
          setContainerCountsReady(cached.containerCountsReady);
          setTotalShipmentCount(cached.totalShipmentCount);
          setTotalStatusCounts(cached.totalStatusCounts || {});
          setAvailableFilterOptions(cached.filterOptions || {});
          setSelectedCustomerId(customerId);
          lastLoadedCustomerRef.current = customerId;
          return;
        }

        const shipmentQuery = new URLSearchParams({ compact: "1", page: String(requestedPage), pageSize: "15" });
        const activeFilters: Record<string, string | string[]> = { search, status: statusFilter, pol: polFilter, pod: podFilter, carrier: carrierFilter, vessel: vesselFilter, consignee: consigneeFilter, modality: modalityFilter, year: yearFilter, month: monthFilter };
        Object.entries(activeFilters).forEach(([key, value]) => {
          const serialized = Array.isArray(value) ? value.join("|") : value;
          if (serialized) shipmentQuery.set(key, serialized);
        });
        const response = await apiFetch(
          `/api/foundry/runtime/shipments?${shipmentQuery.toString()}`,
          { headers: runtimeRequestHeaders(customerId) }
        );
        const data = await response.json().catch(() => ({}));

        if (!prefetch && requestId !== shipmentListRequestRef.current) return;

        if (!response.ok) {
          if (prefetch) return;
          if (data.status === "invalid_customer") {
            const context = await loadBootstrap(true);
            return loadShipments(context, true);
          }
          throw new Error(data.error || "Shipments ophalen mislukt.");
        }

        const resolvedCustomerId = data.customerId || customerId;

        const shipments = Array.isArray(data.items) ? data.items : [];
        const total = Number(data.count || shipments.length);
        const statusSummary = data.statusSummary && typeof data.statusSummary === "object" ? data.statusSummary as Record<string, number> : {};
        const filterOptions = data.filterOptions && typeof data.filterOptions === "object" ? data.filterOptions as Record<string, string[]> : {};
        if (prefetch) {
          setCachedShipmentList(cacheKey, { shipments, containerCountsReady: true, totalShipmentCount: total, totalStatusCounts: statusSummary, filterOptions });
          return;
        }
        setContainerCountsReady(false);
        setShipments(shipments);
        setTotalShipmentCount(total);
        setTotalStatusCounts(statusSummary);
        setAvailableFilterOptions(filterOptions);
        setSelectedCustomerId(resolvedCustomerId);
        lastLoadedCustomerRef.current = resolvedCustomerId;
        setCachedShipmentList(cacheKey, { shipments, containerCountsReady: true, totalShipmentCount: total, totalStatusCounts: statusSummary, filterOptions });

        // Counts are loaded with an individual shipment's details, avoiding a
        // second all-shipment aggregation request during customer switching.
        setContainerCountsReady(true);
      })();

      if (!force && !prefetch) {
        shipmentListInFlightRef.current = { key: cacheKey, promise };
        void promise.finally(() => {
          if (shipmentListInFlightRef.current?.promise === promise) shipmentListInFlightRef.current = null;
        }).catch(() => undefined);
      }

      return promise;
    },
    [apiFetch, loadBootstrap, routePortalKey, runtimeRequestHeaders, search, statusFilter, polFilter, podFilter, carrierFilter, vesselFilter, consigneeFilter, modalityFilter, yearFilter, monthFilter, page]
  );

  const loadPage = useCallback(async (force = false) => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const context = await loadBootstrap();
      await loadShipments(context, force);
    } catch (err: any) {
      setError(err?.message || "Portal laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [loadBootstrap, loadShipments]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (!bootstrap || !selectedCustomerId || loading) {
      return;
    }

    const loadKey = selectedCustomerId;

    if (lastLoadedCustomerRef.current === loadKey) {
      return;
    }

    setLoading(true);
    setError("");

    loadShipments({
      customerId: selectedCustomerId,
    })
      .catch((err: any) => {
        setError(err?.message || "Shipments ophalen mislukt.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [bootstrap, loadShipments, loading, selectedCustomerId]);

  // Detail data is only needed after the user opens a shipment. Do not fetch
  // the first row automatically after every page/customer change: the compact
  // list should become usable without competing with a full container/routing
  // response.
  useEffect(() => {
    const shipmentId = selectedShipmentKey;
    const selectedShipmentIsVisible = shipments.some(
      (shipment, index) => shipmentKey(shipment, index) === shipmentId,
    );
    if (
      !selectedCustomerId ||
      !shipmentId ||
      !selectedShipmentIsVisible ||
      lastLoadedCustomerRef.current !== selectedCustomerId ||
      !userSelectedShipmentRef.current
    ) {
      return;
    }

    const cacheKey = `${routePortalKey || window.location.hostname}:${selectedCustomerId}`;
    const cachedDetails = getCachedShipmentDetails<Record<string, Shipment>>(cacheKey);
    if (cachedDetails?.[shipmentId]) {
      setShipmentDetails(cachedDetails);
      return;
    }
    const prefetchKey = `${cacheKey}:${shipmentId}`;
    if (detailsPrefetchKeyRef.current === prefetchKey) return;
    detailsPrefetchKeyRef.current = prefetchKey;

    let cancelled = false;
    const query = new URLSearchParams({ shipmentId });

    apiFetch(`/api/foundry/runtime/shipments?${query.toString()}`, {
      headers: runtimeRequestHeaders(selectedCustomerId),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok || cancelled || !Array.isArray(data.items) || !data.items[0]) {
          if (!response.ok) detailsPrefetchKeyRef.current = "";
          return;
        }

        const details = { ...(cachedDetails || {}), [shipmentId]: data.items[0] as Shipment };
        setCachedShipmentDetails(cacheKey, details);
        setShipmentDetails(details);
      })
      .catch(() => {
        detailsPrefetchKeyRef.current = "";
      });

    return () => {
      cancelled = true;
    };
  }, [apiFetch, routePortalKey, runtimeRequestHeaders, selectedCustomerId, selectedShipmentKey, selectionVersion, shipments]);

  const refreshData = useCallback(async () => {
    if (!bootstrap) return;

    setRefreshing(true);
    setError("");
    setMessage("");

    try {
      const response = await apiFetch("/api/foundry/runtime/shipments/refresh", {
        method: "POST",
        headers: runtimeRequestHeaders(selectedCustomerId),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || data.message || "Refresh mislukt.");
      }

      setMessage(data.message || "Refresh gestart.");
      await loadShipments({
        customerId: selectedCustomerId,
      }, true);
    } catch (err: any) {
      setError(err?.message || "Refresh mislukt.");
    } finally {
      setRefreshing(false);
    }
  }, [apiFetch, bootstrap, loadShipments, runtimeRequestHeaders, selectedCustomerId]);

  useEffect(() => {
    onAdminToolbarChange?.({
      visible: Boolean(bootstrap?.canSelectCustomer),
      customerId: selectedCustomerId,
      customers,
      loading,
      refreshing,
      onCustomerChange: (customerId) => {
        setSelectedCustomerId(customerId);
        setSearch("");
      },
      onReload: () => { void loadPage(true); },
      onRefresh: refreshData,
    });
  }, [bootstrap?.canSelectCustomer, customers, loadPage, loading, onAdminToolbarChange, refreshing, refreshData, selectedCustomerId]);

  const filterOptions = useMemo(() => {
    const values = (field: keyof Shipment) => Array.from(new Set(shipments.map((shipment) => String(shipment[field] || "").trim()).filter(Boolean))).sort();
    const available = (key: string, fallback: string[]) => Array.from(new Set([...(availableFilterOptions[key] || []), ...fallback])).sort();
    const currentYear = new Date().getFullYear();
    const selectableYears = Array.from({ length: 8 }, (_value, index) => String(currentYear + 1 - index));
    return {
      status: available("status", shipments.map(displayStatus)),
      pol: available("pol", values("pol")),
      pod: available("pod", values("pod")),
      carrier: available("carrier", values("carrier")),
      vessel: available("vessel", values("vessel")),
      consignee: available("consignee", shipments.map(consigneeName).filter(Boolean)),
      // These filters have defaults, so their choices must not be derived
      // solely from the already-filtered result page.
      modality: available("modality", ["OCEAN", "AIR", "ROAD", "RAIL", ...values("modality")]),
      year: Array.from(new Set([...(availableFilterOptions.year || []), ...selectableYears, ...shipments.map(shipmentFilterYear).filter(Boolean)])).sort((left, right) => Number(right) - Number(left)),
      month: available("month", ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"]),
    };
  }, [availableFilterOptions, shipments]);

  const visibleShipments = useMemo(() => shipments
    .filter((shipment) =>
      matchesSearch(shipment, search) &&
      matchesFilter(statusFilter, displayStatus(shipment)) &&
      matchesFilter(polFilter, shipment.pol) &&
      matchesFilter(podFilter, shipment.pod) &&
      matchesFilter(carrierFilter, shipment.carrier) &&
      matchesFilter(vesselFilter, shipment.vessel) &&
      matchesFilter(consigneeFilter, consigneeName(shipment)) &&
      matchesFilter(modalityFilter, shipment.modality) &&
      matchesFilter(yearFilter, shipmentFilterYear(shipment)) &&
      matchesFilter(monthFilter, shipmentFilterMonth(shipment))
    )
    .sort((left, right) => {
      const leftValue = shipmentSortValue(left, sortField);
      const rightValue = shipmentSortValue(right, sortField);
      const comparison = typeof leftValue === "number" && typeof rightValue === "number"
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: "base" });
      return sortDirection === "asc" ? comparison : -comparison;
    }), [shipments, search, statusFilter, polFilter, podFilter, carrierFilter, vesselFilter, consigneeFilter, modalityFilter, yearFilter, monthFilter, sortField, sortDirection]);

  useEffect(() => setPage(1), [search, statusFilter, polFilter, podFilter, carrierFilter, vesselFilter, consigneeFilter, modalityFilter, yearFilter, monthFilter, sortField, sortDirection]);

  useEffect(() => {
    if (!visibleShipments.length) {
      setSelectedShipmentKey("");
      return;
    }

    const selectedStillVisible = visibleShipments.some(
      (shipment, index) => shipmentKey(shipment, index) === selectedShipmentKey
    );

    if (!selectedStillVisible) {
      userSelectedShipmentRef.current = false;
      setSelectedShipmentKey(shipmentKey(visibleShipments[0]));
    }
  }, [selectedShipmentKey, visibleShipments]);

  const selectedShipment = useMemo(() => {
    const shipment = visibleShipments.find(
      (item, index) => shipmentKey(item, index) === selectedShipmentKey
    );
    return shipment ? { ...shipment, ...(shipmentDetails[selectedShipmentKey] || {}) } : null;
  }, [selectedShipmentKey, shipmentDetails, visibleShipments]);

  const statusCounts = useMemo(() => visibleShipments.reduce<Record<string, number>>((counts, shipment) => {
    const status = displayStatus(shipment);
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {}), [visibleShipments]);
  // The server returns the count before pagination; using it here keeps the
  // Total KPI accurate while the table intentionally shows one small page.
  const totalShipments = totalShipmentCount;
  const plannedShipments = totalStatusCounts.Planned ?? statusCounts.Planned ?? 0;
  const inTransitShipments = totalStatusCounts["In Transit"] ?? statusCounts["In Transit"] ?? 0;
  const attentionShipments = totalStatusCounts.Attention ?? statusCounts.Attention ?? 0;
  const arrivedShipments = totalStatusCounts.Arrived ?? statusCounts.Arrived ?? 0;
  const summary = useMemo(() => visibleShipments.reduce(
    (total, shipment) => ({
      shipments: total.shipments + 1,
      containers: total.containers + (shipment.containerCount ?? (Array.isArray(shipment.containers) ? shipment.containers.length : 0)),
      documents: total.documents + (Array.isArray(shipment.documents) ? shipment.documents.length : 0),
      attention: total.attention + (displayStatus(shipment) === "Attention" ? 1 : 0),
    }),
    { shipments: 0, containers: 0, documents: 0, attention: 0 }
  ), [visibleShipments]);
  const pageSize = 15;
  const pageCount = Math.max(1, Math.ceil(totalShipmentCount / pageSize));
  const prefetchNextPage = useCallback(() => {
    if (!bootstrap || loading || !selectedCustomerId || page >= pageCount) return;
    void loadShipments({ customerId: selectedCustomerId, page: page + 1 }, false, true).catch(() => {
      // Prefetch is opportunistic; the explicit Next action still performs a
      // normal request if the background request fails.
    });
  }, [bootstrap, loadShipments, loading, page, pageCount, selectedCustomerId]);
  const tableShipments = visibleShipments;
  const clearFilters = () => {
    setPage(1);
    setSearch("");
    setStatusFilter([]);
    setPolFilter([]);
    setPodFilter([]);
    setCarrierFilter([]);
    setVesselFilter([]);
    setConsigneeFilter([]);
    setModalityFilter([]);
    setYearFilter([]);
    setMonthFilter([]);
  };
  const toggleSort = (field: ShipmentSortField) => {
    if (sortField === field) {
      setSortDirection((direction) => direction === "asc" ? "desc" : "asc");
      return;
    }
    setSortField(field);
    setSortDirection(field === "updatedEta" ? "desc" : "asc");
  };
  const sortableHeader = (label: string, field: ShipmentSortField) => <button type="button" onClick={() => toggleSort(field)} className="flex items-center gap-1 text-left hover:text-orange-500"><span>{label}</span><span className={`text-[10px] ${sortField === field ? "text-orange-500" : "text-slate-400"}`}>{sortField === field ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}</span></button>;
  const selectFilters: ShipmentFilterBarFilter[] = [
    { label: "Status", value: statusFilter, onChange: setStatusFilter, options: filterOptions.status },
    { label: "POL", value: polFilter, onChange: setPolFilter, options: filterOptions.pol },
    { label: "POD", value: podFilter, onChange: setPodFilter, options: filterOptions.pod },
    { label: "Carrier", value: carrierFilter, onChange: setCarrierFilter, options: filterOptions.carrier },
    { label: "Vessel", value: vesselFilter, onChange: setVesselFilter, options: filterOptions.vessel },
    { label: "Consignee", value: consigneeFilter, onChange: setConsigneeFilter, options: filterOptions.consignee },
    { label: "Modality", value: modalityFilter, onChange: setModalityFilter, options: filterOptions.modality },
    { label: "Year", value: yearFilter, onChange: setYearFilter, options: filterOptions.year },
    { label: "Month", value: monthFilter, onChange: setMonthFilter, options: filterOptions.month },
  ];
  const headerKpis: Array<{ label: string; value: number; accent: string; Icon: React.ElementType; calculation: string }> = [
    { label: "Total", value: totalShipments, accent: "#34d399", Icon: Boxes, calculation: "Count of shipment records currently shown after search and all selected filters. Uses shipment reference, status, POL, POD, carrier, vessel and consignee fields." },
    { label: "Planned", value: plannedShipments, accent: "#ec4899", Icon: CalendarDays, calculation: "Count of currently visible shipments whose derived status is Planned. Uses shipment status plus ETD and ETA date fields where status is inferred." },
    { label: "In transit", value: inTransitShipments, accent: "#38bdf8", Icon: Truck, calculation: "Count of currently visible shipments whose derived status is In Transit. Uses shipment status plus ETD and ETA date fields where status is inferred." },
    { label: "Attention", value: attentionShipments, accent: "#f43f5e", Icon: AlertTriangle, calculation: "Count of currently visible shipments whose derived status needs attention. Uses shipment status plus original and updated ETA/ETD fields where status is inferred." },
    { label: "Arrived", value: arrivedShipments, accent: "#f97316", Icon: PackageCheck, calculation: "Count of shipments whose ETA date is before today." },
  ];

  const isInitialDataLoad = loading && shipments.length === 0 && !error;

  if (window.location.pathname !== "/__legacy-customer-portal" && isInitialDataLoad) return (
    <div className="min-h-[calc(100vh-128px)] text-slate-950 dark:text-slate-100">
      <section className="relative overflow-hidden border border-slate-200 bg-white px-6 py-12 dark:border-white/10 dark:bg-slate-900 sm:px-10">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-500 via-sky-400 to-emerald-400" />
        <div className="mx-auto max-w-2xl text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-orange-400/40 bg-orange-500/10 text-orange-400"><ShipWheel className="h-7 w-7 animate-spin" /></div>
          <p className="mt-6 text-[10px] font-black uppercase tracking-[.22em] text-orange-500">Shipment control tower</p>
          <h1 className="mt-2 text-2xl font-black tracking-tight">Preparing your live overview</h1>
          <p className="mt-2 text-sm text-slate-500">Securely loading shipment data for {selectedCustomer?.customerName || "your customer portal"}.</p>
          <div className="relative mx-auto mt-9 flex max-w-md items-center justify-between"><div className="absolute left-6 right-6 top-1/2 h-px -translate-y-1/2 bg-slate-200 dark:bg-white/10" /><div className="absolute left-6 top-1/2 h-px w-1/2 -translate-y-1/2 bg-orange-400 animate-pulse" />{["Connecting", "Loading", "Ready"].map((label, index) => <div key={label} className="relative z-10 flex flex-col items-center gap-2"><span className={`h-4 w-4 rounded-full border-2 border-slate-900 dark:border-slate-900 ${index < 2 ? "bg-orange-400" : "bg-slate-200 dark:bg-slate-700"}`} /><span className="text-[10px] font-bold text-slate-500">{label}</span></div>)}</div>
        </div>
        <div className="mx-auto mt-12 grid max-w-6xl gap-3 md:grid-cols-5">{[0, 1, 2, 3, 4].map((item) => <div key={item} className="border border-slate-200 p-4 dark:border-white/10"><div className="h-2 w-16 animate-pulse rounded bg-slate-200 dark:bg-white/10" /><div className="mt-4 h-6 w-10 animate-pulse rounded bg-slate-200 dark:bg-white/10" /></div>)}</div>
        <div className="mx-auto mt-4 max-w-6xl border border-slate-200 p-5 dark:border-white/10"><div className="space-y-4">{[0, 1, 2, 3].map((item) => <div key={item} className="grid grid-cols-[1.2fr_1fr_1fr_.7fr] gap-6"><span className="h-3 animate-pulse rounded bg-slate-200 dark:bg-white/10" /><span className="h-3 animate-pulse rounded bg-slate-200 dark:bg-white/10" /><span className="h-3 animate-pulse rounded bg-slate-200 dark:bg-white/10" /><span className="h-3 animate-pulse rounded bg-slate-200 dark:bg-white/10" /></div>)}</div></div>
      </section>
    </div>
  );

  // Keep the legacy view available only as a development fallback while the
  // customer runtime uses the denser control-tower workspace by default.
  if (window.location.pathname !== "/__legacy-customer-portal") return (
    <div className="min-h-[calc(100vh-128px)] text-slate-950 dark:text-slate-100">
      <header className="relative overflow-hidden border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900/60">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-orange-400 via-sky-400 to-emerald-400" />
        <div className="grid divide-y divide-slate-200 pt-0.5 dark:divide-slate-800 xl:grid-cols-[minmax(280px,1.4fr)_repeat(5,minmax(132px,1fr))] xl:divide-x xl:divide-y-0">
          <div className="flex flex-col justify-center px-5 py-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-400">Shipment control tower</p>
            <h1 className="mt-1 whitespace-nowrap text-xl font-semibold tracking-tight">{selectedCustomer?.customerName || bootstrap?.portalName || "Customer"}</h1>
          </div>
          {headerKpis.map(({ label, value, accent, Icon, calculation }) => {
            return <article key={label} className="min-w-[132px] bg-slate-950/[0.025] px-5 py-4 dark:bg-slate-900/50">
              <div className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: accent }} />
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
                <MetricInfo>{calculation}</MetricInfo>
              </div>
              <strong className="mt-1 block text-2xl font-semibold tracking-tight leading-none">{value}</strong>
            </article>;
          })}
        </div>
      </header>

      <ShipmentFilterBar filters={selectFilters} search={search} onSearchChange={setSearch} onClear={clearFilters} />

      {error && <div className="mt-3 border-l-4 border-red-400 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 dark:bg-red-500/10 dark:text-red-300">{error}</div>}
      {message && <div className="mt-3 border-l-4 border-emerald-400 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">{message}</div>}

      <section className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1fr)_430px]">
        <article className="overflow-hidden border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900/50">
          {!loading && !visibleShipments.length ? <div className="flex min-h-80 flex-col items-center justify-center p-8 text-center"><AlertTriangle className="h-8 w-8 text-orange-400" /><h2 className="mt-4 text-xl font-black">No shipment data available.</h2><p className="mt-2 text-sm text-slate-500">Adjust your filters or refresh the selected customer data.</p></div> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px] table-fixed text-left text-xs">
                <colgroup>
                  <col className="w-[100px]" /><col className="w-[96px]" /><col className="w-[120px]" />
                  <col className="w-[120px]" /><col className="w-[175px]" /><col className="w-[190px]" />
                  <col className="w-[100px]" /><col className="w-[100px]" /><col className="w-[99px]" />
                </colgroup>
                <thead className="bg-slate-50 text-[9px] uppercase tracking-wide text-slate-500 dark:bg-slate-900/80">
                  <tr>
                    <th className="pl-4 pr-0.5 py-3">{sortableHeader("Reference", "reference")}</th>
                    <th className="px-0.5 py-3 normal-case">Customer reference</th>
                    <th className="px-0.5 py-3">{sortableHeader("Origin", "origin")}</th>
                    <th className="px-0.5 py-3">{sortableHeader("Destination", "destination")}</th>
                    <th className="px-0.5 py-3">{sortableHeader("Consignee", "consignee")}</th>
                    <th className="px-0.5 py-3">{sortableHeader("Carrier / vessel", "carrier")}</th>
                    <th className="px-0.5 py-3">{sortableHeader("ETD", "etd")}</th>
                    <th className="px-0.5 py-3">{sortableHeader("ETA", "updatedEta")}</th>
                    <th className="px-0.5 py-3">{sortableHeader("Status", "status")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                  {(loading ? [] : tableShipments).map((shipment, index) => (
                    <tr key={shipmentKey(shipment, index)} onClick={() => { userSelectedShipmentRef.current = true; setSelectedShipmentKey(shipmentKey(shipment, index)); setSelectionVersion((version) => version + 1); }} className={`cursor-pointer transition hover:bg-orange-50 dark:hover:bg-orange-500/[.06] ${shipmentKey(shipment, index) === selectedShipmentKey ? "bg-orange-50 dark:bg-orange-500/[.08]" : ""}`}>
                      <td className="pl-4 pr-0.5 py-3 font-medium truncate">{shipment.reference || shipment.customerReference || shipment.jordexReference || shipment.blNumber || "-"}</td>
                      <td className="truncate px-0.5 py-3 font-medium" title={shipment.customerReference || undefined}>{shipment.customerReference || "-"}</td>
                      <td className="px-0.5 py-3"><div className="flex items-center gap-1.5 truncate whitespace-nowrap font-bold"><span title={shipment.originPort?.country || "Origin country"}>{countryFlag(shipment.originPort)}</span>{routeLocation(shipment.originPort, shipment.pol)}</div></td>
                      <td className="px-0.5 py-3"><div className="flex items-center gap-1.5 truncate whitespace-nowrap font-medium"><span title={shipment.destinationPort?.country || "Destination country"}>{countryFlag(shipment.destinationPort)}</span>{routeLocation(shipment.destinationPort, shipment.pod)}</div></td>
                      <td className="truncate px-0.5 py-3 font-semibold">{consigneeName(shipment) || "-"}</td>
                      <td className="px-0.5 py-3"><div className="truncate font-bold">{shipment.carrier || "-"}</div><div className="mt-1 truncate text-[9px] text-slate-500">{shipment.vessel || "-"}</div></td>
                      <td className="px-0.5 py-3 font-semibold"><UpdatedDateWithDelta original={shipment.originalEtd} updated={latestEtd(shipment)} /></td>
                      <td className="px-0.5 py-3 font-semibold"><UpdatedDateWithDelta original={shipment.originalEta} updated={latestEta(shipment)} /></td>
                      <td className="px-0.5 py-3"><span className={`inline-flex rounded-full border px-2 py-1 text-[9px] font-black ${statusTone(displayStatus(shipment))}`}>{displayStatus(shipment)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-end gap-3 border-t border-slate-200 p-3 text-xs dark:border-white/10"><button disabled={page === 1} onClick={() => setPage((current) => current - 1)} className="border border-slate-200 px-4 py-2 font-bold disabled:opacity-40 dark:border-white/10">Previous</button><span className="self-center text-slate-500">Page {page} of {pageCount}</span><button disabled={page === pageCount} onPointerEnter={prefetchNextPage} onFocus={prefetchNextPage} onClick={() => setPage((current) => current + 1)} className="border border-slate-200 px-4 py-2 font-bold disabled:opacity-40 dark:border-white/10">Next</button></div>
        </article>

        <aside className="border border-slate-200 bg-white p-3 xl:sticky xl:top-20 xl:max-h-[calc(100vh-5.5rem)] xl:self-start xl:overflow-y-auto dark:border-white/10 dark:bg-slate-900/50">
          {selectedShipment ? <>
            <div className="flex items-start justify-between gap-3"><div><p className="text-[10px] font-black uppercase tracking-[.16em] text-orange-500">Selected shipment</p><div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xl font-normal"><h2 className="font-normal">{selectedShipment.reference || "Shipment"}</h2><p className="max-w-[150px] truncate text-sm font-normal text-slate-500" title={selectedShipment.customerReference || undefined}>{selectedShipment.customerReference || "-"}</p></div></div><span className={`inline-flex rounded-full border px-2 py-1 text-[9px] font-black ${statusTone(displayStatus(selectedShipment))}`}>{displayStatus(selectedShipment)}</span></div>
            <div className="mt-1 grid grid-cols-4 gap-x-2 gap-y-1.5 py-1.5 text-[10px]">
              <div><p className="text-[9px] font-black uppercase text-slate-500">Origin</p><p className="mt-0.5 flex items-center gap-1.5 font-bold"><span title={selectedShipment.originPort?.country || "Origin country"}>{countryFlag(selectedShipment.originPort)}</span>{routeLocation(selectedShipment.originPort, selectedShipment.pol)}</p></div>
              <div><p className="text-[9px] font-black uppercase text-slate-500">Destination</p><p className="mt-0.5 flex items-center gap-1.5 font-bold"><span title={selectedShipment.destinationPort?.country || "Destination country"}>{countryFlag(selectedShipment.destinationPort)}</span>{routeLocation(selectedShipment.destinationPort, selectedShipment.pod)}</p></div>
              <div><p className="text-[9px] font-black uppercase text-slate-500">Carrier</p><p className="mt-0.5 truncate font-bold">{selectedShipment.carrier || "-"}</p><p className="truncate text-[9px] text-slate-500">{selectedShipment.vessel || "-"}</p></div>
              <div><p className="text-[9px] font-black uppercase text-slate-500">Containers</p><p className="mt-0.5 font-bold">{containerCountsReady ? selectedShipment.containerCount ?? (Array.isArray(selectedShipment.containers) ? selectedShipment.containers.length : 0) : "Loading..."}</p></div>
              <div className="col-span-4 grid grid-cols-2 gap-x-2">
                <div><p className="text-[9px] font-black uppercase text-slate-500">ETD</p><p className="mt-0.5 font-bold"><UpdatedDateWithDelta original={selectedShipment.originalEtd} updated={latestEtd(selectedShipment)} /></p></div>
                <div><p className="text-[9px] font-black uppercase text-slate-500">ETA</p><p className="mt-0.5 font-bold"><UpdatedDateWithDelta original={selectedShipment.originalEta} updated={latestEta(selectedShipment)} /></p></div>
              </div>
            </div>
            <div className="mt-1"><ContainerRoutingTimeline containers={selectedShipment.containers} loading={!shipmentDetails[selectedShipmentKey]} /><div className="mt-6"><RouteMap shipment={selectedShipment} /></div></div>
          </> : <div className="flex min-h-64 items-center justify-center text-sm text-slate-500">Select a shipment to view its route and details.</div>}
        </aside>
      </section>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-128px)] text-slate-950 dark:text-slate-100">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-6 dark:border-white/10">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-orange-500 text-white">
              <ShipWheel className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-orange-500">
                Shipment Control Tower
              </p>
              <h1 className="mt-1 text-2xl font-black">
                {bootstrap?.portalName || "Customer Portal"}
              </h1>
            </div>
          </div>
          <p className="mt-3 text-sm font-medium text-slate-500 dark:text-slate-400">
            {selectedCustomer?.customerName || "Customer"} access is resolved
            server-side from Entra groups.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {customers.length > 1 && (
            <select
              value={selectedCustomerId}
              onChange={(event) => {
                setSelectedCustomerId(event.target.value);
                setSearch("");
              }}
              className="h-11 min-w-64 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold outline-none focus:border-orange-400 dark:border-white/10 dark:bg-slate-900"
            >
              {customers.map((customer) => (
                <option key={customer.customerId} value={customer.customerId}>
                  {customer.customerName || customer.customerKey}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            onClick={() => { void loadPage(true); }}
            disabled={loading || refreshing}
            className="inline-flex h-11 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <RefreshCw className="h-4 w-4" />
            Reload
          </button>

          <button
            type="button"
            onClick={refreshData}
            disabled={loading || refreshing || !selectedCustomerId}
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-orange-500 px-4 text-sm font-black text-white transition hover:bg-orange-400 disabled:opacity-50"
          >
            <Truck className="h-4 w-4" />
            {refreshing ? "Refreshing" : "Refresh data"}
          </button>
        </div>
      </header>

      {error && (
        <div className="mt-5 border-l-4 border-red-400 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {message && (
        <div className="mt-5 border-l-4 border-emerald-400 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
          {message}
        </div>
      )}

      <section className="mt-6 grid gap-4 lg:grid-cols-4">
        <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <p className="text-xs font-black uppercase text-slate-500">
            Shipments
          </p>
          <strong className="mt-3 block text-3xl">{summary.shipments}</strong>
        </article>
        <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <p className="text-xs font-black uppercase text-slate-500">
            Containers
          </p>
          <strong className="mt-3 block text-3xl">
            {containerCountsReady ? summary.containers : "Loading…"}
          </strong>
        </article>
        <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <p className="text-xs font-black uppercase text-slate-500">
            Documents
          </p>
          <strong className="mt-3 block text-3xl">{summary.documents}</strong>
        </article>
        <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <p className="text-xs font-black uppercase text-slate-500">
            Attention
          </p>
          <strong className="mt-3 block text-3xl">{summary.attention}</strong>
        </article>
      </section>

      {selectedShipment && (
        <section className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(420px,.9fr)]">
          <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-4 dark:border-white/10">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-orange-500">
                  Selected shipment
                </p>
                <h2 className="mt-1 text-xl font-black">
                  {selectedShipment.reference || selectedShipment.customerReference || "Shipment"}
                </h2>
                <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                  {selectedShipment.jordexReference || selectedShipment.id || "No Jordex reference"}
                </p>
              </div>
              <span className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-black ${statusTone(selectedShipment.status)}`}>
                {selectedShipment.status || "Unknown"}
              </span>
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div className="space-y-4">
                <div className="flex gap-3">
                  <Anchor className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                  <div>
                    <p className="text-xs font-black uppercase text-slate-500">Transport</p>
                    <p className="mt-1 font-bold">{selectedShipment.carrier || "Carrier not available"}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{selectedShipment.vessel || "Vessel not available"}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                  <div>
                    <p className="text-xs font-black uppercase text-slate-500">Cargo</p>
                    <p className="mt-1 font-bold">
                      {containerCountsReady
                        ? `${selectedShipment.containerCount ?? 0} containers`
                        : "Container count loading…"}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {[selectedShipment.modality, selectedShipment.loadType].filter(Boolean).join(" · ") || "Mode not available"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
                  <div>
                    <p className="text-xs font-black uppercase text-slate-500">Departure</p>
                    <p className="mt-1 font-bold">{formatDate(selectedShipment.etd)}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{portLabel(selectedShipment.originPort, selectedShipment.pol)}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
                  <div>
                    <p className="text-xs font-black uppercase text-slate-500">Arrival</p>
                    <p className="mt-1 font-bold">{formatDate(selectedShipment.eta)}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{portLabel(selectedShipment.destinationPort, selectedShipment.pod)}</p>
                  </div>
                </div>
              </div>
            </div>
            <ContainerRoutingTimeline containers={selectedShipment.containers} loading={!shipmentDetails[selectedShipmentKey]} />
          </article>

          <RouteMap shipment={selectedShipment} />
        </section>
      )}

      <section className="mt-6 rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4 dark:border-white/10">
          <label className="flex h-11 min-w-80 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 dark:border-white/10 dark:bg-slate-950">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search reference, port, carrier, vessel..."
              className="w-full bg-transparent text-sm font-semibold outline-none"
            />
          </label>

          <div className="inline-flex items-center gap-2 text-sm font-bold text-slate-500">
            <Boxes className="h-4 w-4" />
            {loading ? "Loading" : `${visibleShipments.length} visible · select a shipment for details`}
          </div>
        </div>

        {!loading && visibleShipments.length === 0 ? (
          <div className="flex min-h-80 flex-col items-center justify-center p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-orange-400" />
            <h2 className="mt-4 text-xl font-black">
              No shipment data available.
            </h2>
            <p className="mt-2 max-w-xl text-sm font-medium text-slate-500 dark:text-slate-400">
              Load data in Foundry or start a refresh for the selected
              customer.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-950/70">
                <tr>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">POL</th>
                  <th className="px-4 py-3">POD</th>
                  <th className="px-4 py-3">Carrier</th>
                  <th className="px-4 py-3">Vessel</th>
                  <th className="px-4 py-3">ETD</th>
                  <th className="px-4 py-3">ETA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                {(loading ? [] : visibleShipments).slice(0, 250).map(
                  (shipment, index) => (
                    <tr
                      key={shipmentKey(shipment, index)}
                      onClick={() => { userSelectedShipmentRef.current = true; setSelectedShipmentKey(shipmentKey(shipment, index)); setSelectionVersion((version) => version + 1); }}
                      className={`cursor-pointer transition hover:bg-orange-50 dark:hover:bg-orange-500/[0.06] ${
                        shipmentKey(shipment, index) === selectedShipmentKey
                          ? "bg-orange-50 dark:bg-orange-500/[0.08]"
                          : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-black">
                          {shipment.reference ||
                            shipment.customerReference ||
                            "-"}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {shipment.jordexReference || shipment.id || ""}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-black ${statusTone(
                            shipment.status
                          )}`}
                        >
                          {shipment.status || "Unknown"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        {shipment.pol || "-"}
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        {shipment.pod || "-"}
                      </td>
                      <td className="px-4 py-3">{shipment.carrier || "-"}</td>
                      <td className="px-4 py-3">{shipment.vessel || "-"}</td>
                      <td className="px-4 py-3">{formatDate(shipment.etd)}</td>
                      <td className="px-4 py-3">{formatDate(shipment.eta)}</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
